// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO: "Ask someone" on the agent's question card (design/agents-side-bar r2-q1, r2-q2). Pick a
// person you can message (what they do from their team roles, their status and "on leave"-style
// custom status when Konvo knows them), add a note if you like, and Konvo posts the question into
// your chat with them. A popover on desktop, a bottom sheet on phones.

import Channels from '@app/features/channel/state/Channels';
import Guilds from '@app/features/guild/state/Guilds';
import styles from '@app/features/konvo_agents/components/AgentAsk.module.css';
import {RunningRing} from '@app/features/konvo_agents/components/AgentBits';
import {agentErrorMessage} from '@app/features/konvo_agents/components/AgentsCopy';
import AgentAsks from '@app/features/konvo_agents/state/AgentAsks';
import {firstName} from '@app/features/konvo_agents/state/AgentAsksModel';
import {ensureOrgPeopleLoaded, getOrgGuildId, getOrgPeople} from '@app/features/konvo_people/utils/OrgPeople';
import GuildMembers from '@app/features/member/state/GuildMembers';
import {usePresenceCustomStatus} from '@app/features/presence/hooks/usePresenceCustomStatus';
import {BottomSheet} from '@app/features/ui/bottom_sheet/BottomSheet';
import * as ToastCommands from '@app/features/ui/commands/ToastCommands';
import {StatusAwareAvatar} from '@app/features/ui/components/StatusAwareAvatar';
import {Popout} from '@app/features/ui/popover/PopoverPopout';
import type {User} from '@app/features/user/models/User';
import Users from '@app/features/user/state/Users';
import {ChannelTypes} from '@konvo/constants/src/ChannelConstants';
import type {AgentAsk} from '@konvo/schema/src/domains/konvo/Agents';
import {Trans, useLingui} from '@lingui/react/macro';
import {IconCheck, IconMessageCircle, IconSearch, IconSend, IconUserQuestion} from '@tabler/icons-react';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {type ReactNode, useEffect, useMemo, useState} from 'react';

/** People the owner can message: their DMs first (most recent first), then the organisation. */
export function askablePeople(): Array<User> {
	const me = Users.currentUser?.id ?? null;
	const seen = new Set<string>();
	const out: Array<User> = [];
	const add = (user: User | null | undefined) => {
		if (!user || user.bot || user.system || user.id === me || seen.has(user.id)) return;
		seen.add(user.id);
		out.push(user);
	};
	for (const channel of Channels.dmChannels) {
		if (channel.type !== ChannelTypes.DM) continue;
		add(Users.getUser(channel.recipientIds[0] ?? ''));
	}
	const rest = getOrgPeople().filter((user) => !seen.has(user.id));
	rest.sort((a, b) => a.displayName.localeCompare(b.displayName));
	for (const user of rest) add(user);
	return out;
}

/** What someone does, from their roles in the organisation (or any team you share): up to two. */
export function personRoles(userId: string): Array<string> {
	const org = getOrgGuildId();
	const guilds = org ? [Guilds.getGuild(org)].filter(Boolean) : Guilds.getGuilds();
	for (const guild of guilds) {
		if (!guild) continue;
		const member = GuildMembers.getMember(guild.id, userId);
		if (!member?.roles.size) continue;
		const roles = [...member.roles]
			.map((id) => guild.getRole(id))
			.filter((role) => role && role.id !== guild.id && role.name.trim())
			.sort((a, b) => b!.position - a!.position)
			.map((role) => role!.name.trim());
		if (roles.length) return roles.slice(0, 2);
	}
	return [];
}

/** Words in a custom status that say someone is away. */
const AWAY = /\b(leave|holiday|vacation|out of office|ooo|away|off sick|sick)\b/i;

const PersonRow = observer(({user, on, onPick}: {user: User; on: boolean; onPick: (user: User) => void}) => {
	const status = usePresenceCustomStatus({userId: user.id});
	const roles = personRoles(user.id);
	const statusText = status?.text?.trim() ?? '';
	const away = AWAY.test(statusText);
	return (
		<button
			type="button"
			role="option"
			aria-selected={on}
			className={clsx(styles.person, on && styles.personOn)}
			onClick={() => onPick(user)}
			data-flx="konvo-agents.ask.person"
		>
			<StatusAwareAvatar user={user} size={36} />
			<span className={styles.personText}>
				<span className={styles.personName}>{user.displayName}</span>
				{(roles.length > 0 || statusText) && (
					<span className={styles.personAbout} data-flx="konvo-agents.ask.person-about">
						{roles.join(', ')}
						{roles.length > 0 && statusText ? ' · ' : ''}
						{statusText && <span className={clsx(away && styles.away)}>{statusText}</span>}
					</span>
				)}
			</span>
			{on && <IconCheck size={17} stroke={2.2} className={styles.personCheck} aria-hidden />}
		</button>
	);
});

const AskPicker = observer(
	({
		machineId,
		threadId,
		requestId,
		sheet,
		onDone,
	}: {
		machineId: string;
		threadId: string;
		requestId: string;
		sheet: boolean;
		onDone: (ask: AgentAsk | null) => void;
	}) => {
		const {t, i18n} = useLingui();
		const [query, setQuery] = useState('');
		const [picked, setPicked] = useState<User | null>(null);
		const [noteOpen, setNoteOpen] = useState(false);
		const [note, setNote] = useState('');
		const [sending, setSending] = useState(false);
		useEffect(() => ensureOrgPeopleLoaded(), []);
		const people = askablePeople();
		const shown = useMemo(() => {
			const words = query.toLowerCase().split(/\s+/).filter(Boolean);
			if (!words.length) return people;
			return people.filter((user) => {
				const hay = `${user.displayName} ${user.username} ${personRoles(user.id).join(' ')}`.toLowerCase();
				return words.every((word) => hay.includes(word));
			});
		}, [people, query]);

		const send = async () => {
			if (!picked || sending) return;
			setSending(true);
			try {
				const ask = await AgentAsks.ask(machineId, threadId, requestId, picked.id, note);
				const person = picked.displayName;
				ToastCommands.success(
					t({
						message: `Asked ${person}. Their answer goes to the agent.`,
						comment: 'Agents: toast after the question was sent to a colleague in your chat with them.',
					}),
				);
				onDone(ask);
			} catch (error) {
				ToastCommands.error(agentErrorMessage(i18n, error));
				setSending(false);
			}
		};

		const name = picked ? firstName(picked.displayName) : '';
		return (
			<div className={clsx(styles.picker, sheet && styles.pickerSheet)} data-flx="konvo-agents.ask.picker">
				<label className={styles.search}>
					<IconSearch size={17} stroke={1.8} aria-hidden />
					<input
						className={styles.searchInput}
						value={query}
						onChange={(event) => setQuery(event.target.value)}
						placeholder={t({message: 'Ask who?', comment: 'Agents: search field for the person to ask.'})}
						aria-label={t`Search people`}
						// biome-ignore lint/a11y/noAutofocus: the picker opens to type a name.
						autoFocus={!sheet}
						data-flx="konvo-agents.ask.search"
					/>
				</label>
				<div className={styles.people} role="listbox" aria-label={t`People`}>
					{shown.length === 0 ? (
						<div className={styles.empty}>
							<Trans comment="Agents: nobody matches the search in the Ask someone picker.">Nobody by that name.</Trans>
						</div>
					) : (
						shown
							.slice(0, 60)
							.map((user) => <PersonRow key={user.id} user={user} on={picked?.id === user.id} onPick={setPicked} />)
					)}
				</div>
				{noteOpen && (
					<textarea
						className={styles.note}
						value={note}
						maxLength={1000}
						onChange={(event) => setNote(event.target.value)}
						placeholder={t({
							message: 'A note for them, sent before the question',
							comment: 'Agents: placeholder of the optional note sent with a question to a colleague.',
						})}
						aria-label={t`Note`}
						// biome-ignore lint/a11y/noAutofocus: opened to write the note.
						autoFocus
						data-flx="konvo-agents.ask.note"
					/>
				)}
				<div className={styles.hint}>
					<IconMessageCircle size={15} stroke={1.7} aria-hidden />
					<span>
						<Trans comment="Agents: how asking a colleague works, under the people list.">
							They get the question in your chat with them. Their answer goes to the agent, marked as theirs.
						</Trans>
					</span>
				</div>
				<div className={styles.pickerActions}>
					{!noteOpen && (
						<button
							type="button"
							className={styles.plain}
							onClick={() => setNoteOpen(true)}
							data-flx="konvo-agents.ask.add-note"
						>
							<Trans comment="Agents: button that opens a note field in the Ask someone picker.">Add a note</Trans>
						</button>
					)}
					<button
						type="button"
						className={styles.primary}
						disabled={!picked || sending}
						onClick={() => void send()}
						data-flx="konvo-agents.ask.send"
					>
						{sending ? (
							<RunningRing size={14} />
						) : (
							<>
								<IconSend size={16} stroke={1.9} aria-hidden />
								{picked ? (
									<Trans comment="Agents: send the question to the picked person, e.g. Ask Lerato.">Ask {name}</Trans>
								) : (
									<Trans comment="Agents: send button before anyone is picked.">Ask</Trans>
								)}
							</>
						)}
					</button>
				</div>
			</div>
		);
	},
);

/** The pill beside the answer box: "Ask someone", or who it is out with ("Asked Lerato · waiting"). */
export const AgentAskButton = observer(
	({
		machineId,
		threadId,
		requestId,
		waiting,
		phone,
		disabled,
	}: {
		machineId: string;
		threadId: string;
		requestId: string;
		waiting: ReadonlyArray<AgentAsk>;
		phone: boolean;
		disabled?: boolean;
	}) => {
		const {t} = useLingui();
		const [sheetOpen, setSheetOpen] = useState(false);
		const names = waiting.map((ask) => firstName(ask.personName)).join(', ');
		const label: ReactNode = waiting.length ? (
			<>
				<span className={styles.askButtonText}>
					<Trans comment="Agents: the question is out with these people, e.g. Asked Lerato.">Asked {names}</Trans>
				</span>
				<span className={styles.waiting}>
					<Trans comment="Agents: after 'Asked Lerato': they have not answered yet.">· waiting</Trans>
				</span>
			</>
		) : (
			<span className={styles.askButtonText}>
				<Trans comment="Agents: button on the agent's question that sends it to a colleague.">Ask someone</Trans>
			</span>
		);
		const title = waiting.length
			? t({
					message: 'Ask someone else',
					comment: 'Agents: tooltip on the Asked … waiting pill; it opens the picker again.',
				})
			: t({
					message: 'Send this question to a colleague',
					comment: 'Agents: tooltip on the Ask someone button.',
				});
		if (phone)
			return (
				<>
					<button
						type="button"
						className={styles.askButton}
						onClick={() => setSheetOpen(true)}
						disabled={disabled}
						title={title}
						data-flx="konvo-agents.thread.question.ask"
					>
						<IconUserQuestion size={17} stroke={1.8} aria-hidden />
						{label}
					</button>
					<BottomSheet
						isOpen={sheetOpen}
						onClose={() => setSheetOpen(false)}
						title={t({message: 'Ask someone', comment: 'Agents: title of the sheet that picks a colleague to ask.'})}
						snapPoints={[0, 0.85]}
						initialSnap={1}
					>
						{sheetOpen && (
							<AskPicker
								machineId={machineId}
								threadId={threadId}
								requestId={requestId}
								sheet
								onDone={() => setSheetOpen(false)}
							/>
						)}
					</BottomSheet>
				</>
			);
		return (
			<Popout
				position="top-end"
				offsetMainAxis={8}
				render={({onClose}) => (
					<AskPicker
						machineId={machineId}
						threadId={threadId}
						requestId={requestId}
						sheet={false}
						onDone={() => onClose()}
					/>
				)}
			>
				<button
					type="button"
					className={styles.askButton}
					disabled={disabled}
					aria-haspopup="dialog"
					title={title}
					data-flx="konvo-agents.thread.question.ask"
				>
					<IconUserQuestion size={17} stroke={1.8} aria-hidden />
					{label}
				</button>
			</Popout>
		);
	},
);
